import { configureStore, createListenerMiddleware } from '@reduxjs/toolkit';
import { authApi } from './api/authApi';
import { onboardingApi } from './api/onboardingApi';
import { featuresApi } from './api/featuresApi';
import { planApi } from './api/planApi';
import { analyticsApi } from './api/analyticsApi';
import { payoutReviewApi } from './api/payoutReviewApi';
import { adminOpsApi } from './api/adminOpsApi';
import { notificationsApi } from './api/notificationsApi';
import authReducer from './slices/authSlice';

// Every API whose cached data belongs to the signed-in account. authApi is left
// out: it holds only the login/register mutations themselves, and resetting it
// mid-login would drop the result the login screen is waiting on.
const ACCOUNT_SCOPED_APIS = [
  onboardingApi,
  featuresApi,
  planApi,
  analyticsApi,
  payoutReviewApi,
  adminOpsApi,
  notificationsApi,
];

const userIdOf = (state) => state.auth?.user?.id ?? state.auth?.user?.userId ?? null;

/**
 * Drop all cached account data whenever the signed-in user changes.
 *
 * RTK Query keys its cache by endpoint and arguments, not by who asked, and
 * nothing ever cleared it. So after logging out and signing in as someone else
 * in the same tab, the new account was served the previous account's answers:
 * a brand-new merchant got the last user's "onboarding completed" and skipped
 * the wizard, and the same applied to plan, billing, features, analytics and
 * notifications. Clearing on every change of user id -- logout (id -> null),
 * login or signup (null -> id), or a direct switch -- covers all of them.
 */
const accountSwitch = createListenerMiddleware();
accountSwitch.startListening({
  predicate: (_action, current, previous) => userIdOf(current) !== userIdOf(previous),
  effect: (_action, api) => {
    ACCOUNT_SCOPED_APIS.forEach((a) => api.dispatch(a.util.resetApiState()));
  },
});

export const store = configureStore({
  reducer: {
    [authApi.reducerPath]: authApi.reducer,
    [onboardingApi.reducerPath]: onboardingApi.reducer,
    [featuresApi.reducerPath]: featuresApi.reducer,
    [planApi.reducerPath]: planApi.reducer,
    [analyticsApi.reducerPath]: analyticsApi.reducer,
    [payoutReviewApi.reducerPath]: payoutReviewApi.reducer,
    [adminOpsApi.reducerPath]: adminOpsApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    auth: authReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      // Listener first, so it sees each auth change as soon as it lands.
      .prepend(accountSwitch.middleware)
      .concat(authApi.middleware, onboardingApi.middleware, featuresApi.middleware, planApi.middleware, analyticsApi.middleware, payoutReviewApi.middleware, adminOpsApi.middleware, notificationsApi.middleware),
});

export default store;
